import type { PreviewMaps } from '../worker/protocol';
import type { GlJob } from './gl-job';
import { FRAG_BASE, FRAG_BLIT, FRAG_PRINT, VERT } from './gl-shaders';

/**
 * The GPU mockup preview: the same composite `renderMockup` builds pixel by pixel in the worker, as
 * one pass per print over a pair of ping-pong framebuffers. The renderer owns nothing but its
 * context, its textures and its three programs — no caching, no job memory, no rAF loop — so the
 * caller decides when a frame is worth drawing and Task 4 can compare a single frame against the CPU.
 *
 * `setMaps` is the expensive call (five map uploads, a photo upload and two framebuffers); `render`
 * is meant to be cheap enough for a drag.
 */
export interface MockupGl {
  /** Replace every map texture and resize the FBOs and the canvas to `maps.width × maps.height`.
   *  Includes the garment's drop shadow (`maps.shadow`, absent for a non-separable blank). */
  setMaps(maps: PreviewMaps): void;
  /** Replace the drop-shadow map (R8). `null` — a blank whose garment could not be separated from
   *  its backdrop — leaves a 1×1 zero texture bound, so the base pass can always `texelFetch` it;
   *  such a job draws no backdrop at all (`glJobFor` forces `backdrop.kind` 0). Called by
   *  `setMaps`; exposed so a caller can replace one map without re-uploading the other five. */
  setShadow(map: Uint8Array | null, width: number, height: number): void;
  /** Upload (or replace) the art a print names by id. The caller keeps ownership of the bitmap. */
  setArt(id: string, bitmap: ImageBitmap): void;
  dropArt(id: string): void;
  /** Upload (or replace) one scene backdrop's cover, keyed by `sceneCoverKey` — already cut to the
   *  maps' exact size by the worker, because the pass fetches it pixel for pixel. The caller keeps
   *  ownership of the bitmap. */
  setScene(key: string, bitmap: ImageBitmap): void;
  dropScene(key: string): void;
  /** Draw one frame. False when there is nothing to draw with (no maps yet) or the context is gone. */
  render(job: GlJob): boolean;
  /** The last rendered frame as top-down RGBA (row 0 = the top of the image), read from the FBO. */
  readPixels(): Uint8ClampedArray;
  readonly canvas: HTMLCanvasElement | OffscreenCanvas;
  dispose(): void;
  /** Called once when the context is lost. The renderer does NOT rebuild itself — the caller makes
   *  a new one (the textures, programs and FBOs of a lost context are all gone). */
  onContextLost(fn: () => void): void;
}

/** `preserveDrawingBuffer` so the caller can `drawImage` the canvas (or read it) after `render`
 *  returns; no alpha/depth/stencil/AA because every pass writes an opaque full-screen triangle. */
const CTX_ATTRS: WebGLContextAttributes = { alpha: false, premultipliedAlpha: false, antialias: false, preserveDrawingBuffer: true, depth: false, stencil: false };

function compile(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const s = gl.createShader(type);
  if (!s) throw new Error('mockup gl: could not create a shader');
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(s) ?? '';
    gl.deleteShader(s);
    // A shader that does not compile is a bug in this file, not a browser we can fall back for.
    throw new Error(`mockup gl: shader failed to compile: ${log}`);
  }
  return s;
}

function link(gl: WebGL2RenderingContext, vertSrc: string, fragSrc: string): WebGLProgram {
  const vs = compile(gl, gl.VERTEX_SHADER, vertSrc), fs = compile(gl, gl.FRAGMENT_SHADER, fragSrc);
  const p = gl.createProgram();
  if (!p) throw new Error('mockup gl: could not create a program');
  gl.attachShader(p, vs);
  gl.attachShader(p, fs);
  gl.linkProgram(p);
  gl.deleteShader(vs);   // the program keeps its own reference until it is deleted
  gl.deleteShader(fs);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(p) ?? '';
    gl.deleteProgram(p);
    throw new Error(`mockup gl: program failed to link: ${log}`);
  }
  return p;
}

type Locs = Record<string, WebGLUniformLocation | null>;
const locsOf = (gl: WebGL2RenderingContext, p: WebGLProgram, names: readonly string[]): Locs =>
  Object.fromEntries(names.map((n) => [n, gl.getUniformLocation(p, n)]));

/** NEAREST/CLAMP with no mipmaps for every texture: the maps are read with `texelFetch` at integer
 *  coordinates and the art's bilinear tap is done in the shader (the CPU's `sampleArt`), so hardware
 *  filtering could only add a second, differently-rounded interpolation. */
function newTex(gl: WebGL2RenderingContext): WebGLTexture {
  const t = gl.createTexture();
  if (!t) throw new Error('mockup gl: could not create a texture');
  gl.bindTexture(gl.TEXTURE_2D, t);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  return t;
}

/** gx/gy arrive as two planar arrays; RG8_SNORM wants them interleaved so one fetch gives both. */
function interleave(gx: Int8Array, gy: Int8Array): Int8Array {
  const n = Math.min(gx.length, gy.length), out = new Int8Array(n * 2);
  for (let i = 0; i < n; i++) { out[i * 2] = gx[i]; out[i * 2 + 1] = gy[i]; }
  return out;
}

interface Target { fb: WebGLFramebuffer; tex: WebGLTexture }

const BASE_U = ['uPhoto', 'uMask', 'uShadow', 'uScene', 'uGarment', 'uTarget', 'uRecolor', 'uBackdrop', 'uBackA', 'uBackB', 'uShadowK', 'uHeight'] as const;
const PRINT_U = ['uBase', 'uMask', 'uLuma', 'uGrad', 'uResidual', 'uArt', 'uHinv', 'uAreaIn', 'uRect', 'uRot', 'uArtSize', 'uLook', 'uLmid', 'uDispPx'] as const;
const BLIT_U = ['uSrc', 'uHeight'] as const;

export function createMockupGl(canvas: HTMLCanvasElement | OffscreenCanvas): MockupGl | null {
  let ctx: WebGL2RenderingContext | null = null;
  try {
    // The union's two `getContext` overloads do not resolve together; both accept the attributes.
    ctx = (canvas as HTMLCanvasElement).getContext('webgl2', CTX_ATTRS) as WebGL2RenderingContext | null;
  } catch {
    ctx = null;   // some browsers throw instead of returning null when they cannot make a context
  }
  if (!ctx) return null;
  const gl = ctx;
  // Dithering is ENABLED by default in GL, and every pass here writes 8-bit colour: left on, the
  // driver is free to add its own ±1 of ordered noise to the values the shaders computed, which is
  // exactly the difference a pixel-for-pixel comparison against the CPU compositor cannot explain.
  gl.disable(gl.DITHER);

  const baseProg = link(gl, VERT, FRAG_BASE), printProg = link(gl, VERT, FRAG_PRINT), blitProg = link(gl, VERT, FRAG_BLIT);
  const uBase = locsOf(gl, baseProg, BASE_U), uPrint = locsOf(gl, printProg, PRINT_U), uBlit = locsOf(gl, blitProg, BLIT_U);
  // No vertex buffer at all: the vertex shader derives the triangle from gl_VertexID. The VAO exists
  // only so no pass can inherit attribute state from whatever else touched the default one.
  const vao = gl.createVertexArray();
  // Sampler-unit assignments never change, so they are set once: 0 = photo/base, 1 = mask,
  // 2 = luma, 3 = grad, 4 = residual, 5 = art, 6 = shadow, 7 = scene.
  gl.useProgram(baseProg);
  gl.uniform1i(uBase.uPhoto, 0); gl.uniform1i(uBase.uMask, 1);
  gl.uniform1i(uBase.uShadow, 6); gl.uniform1i(uBase.uScene, 7);
  gl.useProgram(printProg);
  gl.uniform1i(uPrint.uBase, 0); gl.uniform1i(uPrint.uMask, 1); gl.uniform1i(uPrint.uLuma, 2);
  gl.uniform1i(uPrint.uGrad, 3); gl.uniform1i(uPrint.uResidual, 4); gl.uniform1i(uPrint.uArt, 5);
  gl.useProgram(blitProg);
  gl.uniform1i(uBlit.uSrc, 0);

  let width = 0, height = 0, ready = false;
  let photo: WebGLTexture | null = null, mask: WebGLTexture | null = null, luma: WebGLTexture | null = null;
  let grad: WebGLTexture | null = null, residual: WebGLTexture | null = null, shadow: WebGLTexture | null = null;
  const arts = new Map<string, WebGLTexture>();
  /** One cover per `sceneCoverKey`, exactly as `arts` holds one texture per art id. */
  const scenes = new Map<string, WebGLTexture>();
  const targets: (Target | null)[] = [null, null];
  /** Which target holds the finished frame — what `readPixels` reads and the blit shows. */
  let last = 0;
  let lost = false, disposed = false;
  const lostFns: Array<() => void> = [];

  const markLost = (): void => {
    if (lost) return;
    lost = true;
    for (const fn of lostFns) fn();
  };
  const onLost = (e: Event): void => {
    e.preventDefault();   // without this the browser will never offer a restored context
    markLost();
  };
  // Nothing to do on restore: every GL object this renderer holds died with the old context, so the
  // caller disposes it and builds another (which is also how it gets its maps and arts back).
  const onRestored = (): void => {};
  const target = canvas as EventTarget;
  target.addEventListener('webglcontextlost', onLost);
  target.addEventListener('webglcontextrestored', onRestored);

  const dead = (): boolean => {
    if (disposed) return true;
    if (lost) return true;
    if (gl.isContextLost()) { markLost(); return true; }
    return false;
  };

  const deleteTargets = (): void => {
    for (let i = 0; i < 2; i++) {
      const t = targets[i];
      if (!t) continue;
      gl.deleteFramebuffer(t.fb);
      gl.deleteTexture(t.tex);
      targets[i] = null;
    }
  };
  const makeTarget = (): Target => {
    const tex = newTex(gl);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    const fb = gl.createFramebuffer();
    if (!fb) throw new Error('mockup gl: could not create a framebuffer');
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    if (status !== gl.FRAMEBUFFER_COMPLETE) throw new Error(`mockup gl: framebuffer incomplete (0x${status.toString(16)})`);
    return { fb, tex };
  };

  /** The unpack state every upload here wants: tight rows (R8 and RGB8_SNORM rows are not 4-byte
   *  aligned), no flip, no premultiply and no colour conversion — the textures must hold exactly the
   *  bytes the worker measured, in the order it measured them. */
  const unpackRaw = (): void => {
    gl.activeTexture(gl.TEXTURE0);   // every upload below lands on a known unit
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 0);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, 0);
    gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);
  };

  const bindUnit = (unit: number, tex: WebGLTexture | null): void => {
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, tex);
  };

  // One texel each, for the two samplers the base pass reads only when it is drawing a backdrop. A
  // sampler bound to NOTHING is incomplete in GL and reading one is undefined behaviour — even on
  // the frames where `uBackdrop` is 0 and the fetched value could not change the result — so a
  // non-separable blank (no shadow map) and every non-scene backdrop get these instead of null.
  // Built eagerly, before anything else binds a unit: creating them lazily inside `render` would
  // leave the new texture bound to whatever unit was active at the time.
  unpackRaw();
  const zeroShadow = newTex(gl);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, 1, 1, 0, gl.RED, gl.UNSIGNED_BYTE, new Uint8Array(1));
  const zeroScene = newTex(gl);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array(4));

  const setShadow = (map: Uint8Array | null, w: number, h: number): void => {
    if (dead()) return;
    if (shadow) { gl.deleteTexture(shadow); shadow = null; }
    if (!map) return;   // `zeroShadow` stands in, so the pass can always fetch (see above)
    unpackRaw();        // R8 rows are not 4-byte aligned: UNPACK_ALIGNMENT 1 or every row shears
    shadow = newTex(gl);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, w, h, 0, gl.RED, gl.UNSIGNED_BYTE, map);
  };

  const setMaps = (maps: PreviewMaps): void => {
    if (dead()) return;
    width = maps.width; height = maps.height;
    // The canvas has to match the FBOs: the blit draws one full-screen triangle at this viewport.
    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;
    for (const t of [photo, mask, luma, grad, residual]) if (t) gl.deleteTexture(t);
    photo = mask = luma = grad = residual = null;
    deleteTargets();
    ready = false;
    unpackRaw();
    photo = newTex(gl);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, maps.photo);
    mask = newTex(gl);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, width, height, 0, gl.RED, gl.UNSIGNED_BYTE, maps.mask);
    // R32F sampled with NEAREST needs no extension; LINEAR would (and would be wrong here anyway).
    luma = newTex(gl);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.R32F, width, height, 0, gl.RED, gl.FLOAT, maps.lumaBlur);
    grad = newTex(gl);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RG8_SNORM, width, height, 0, gl.RG, gl.BYTE, interleave(maps.gx, maps.gy));
    residual = newTex(gl);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB8_SNORM, width, height, 0, gl.RGB, gl.BYTE, maps.residual);
    // Absent for a blank whose garment could not be told from its backdrop — which is exactly the
    // blank whose backdrop `renderMockup` ignores, so nothing here will ask for it.
    setShadow(maps.shadow ?? null, width, height);
    targets[0] = makeTarget();
    targets[1] = makeTarget();
    last = 0;
    ready = true;
  };

  const setArt = (id: string, bitmap: ImageBitmap): void => {
    if (dead()) return;
    const old = arts.get(id);
    if (old) gl.deleteTexture(old);   // replacing, not layering: one texture per art id
    unpackRaw();
    const t = newTex(gl);
    // Straight (un-premultiplied) alpha: the shader premultiplies per tap, as `sampleArt` does.
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, bitmap.width, bitmap.height, 0, gl.RGBA, gl.UNSIGNED_BYTE, bitmap);
    arts.set(id, t);
  };

  const dropArt = (id: string): void => {
    const t = arts.get(id);
    if (!t) return;
    if (!disposed && !lost) gl.deleteTexture(t);
    arts.delete(id);
  };

  const setScene = (key: string, bitmap: ImageBitmap): void => {
    if (dead()) return;
    const old = scenes.get(key);
    if (old) gl.deleteTexture(old);   // one texture per cover key, replaced rather than layered
    unpackRaw();
    // Straight (un-premultiplied) alpha like the art, though a cover is opaque: the worker flattens
    // every scene onto white before it cuts one, so there is no alpha left to premultiply.
    const t = newTex(gl);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, bitmap.width, bitmap.height, 0, gl.RGBA, gl.UNSIGNED_BYTE, bitmap);
    scenes.set(key, t);
  };

  const dropScene = (key: string): void => {
    const t = scenes.get(key);
    if (!t) return;
    if (!disposed && !lost) gl.deleteTexture(t);
    scenes.delete(key);
  };

  const render = (job: GlJob): boolean => {
    if (dead() || !ready) return false;
    const a = targets[0], b = targets[1];
    if (!a || !b) return false;
    gl.bindVertexArray(vao);
    gl.disable(gl.BLEND); gl.disable(gl.DEPTH_TEST); gl.disable(gl.SCISSOR_TEST); gl.disable(gl.CULL_FACE);
    gl.viewport(0, 0, width, height);

    // Pass 0: the garment and the backdrop behind it, into A.
    gl.bindFramebuffer(gl.FRAMEBUFFER, a.fb);
    gl.useProgram(baseProg);
    const back = job.backdrop;
    // A scene whose cover this renderer is not holding draws NO backdrop rather than a stale or
    // empty one — the same rule `render` applies to a print whose art has not been uploaded, and
    // `glJobFor` has normally forced kind 0 for it already (the `covers` set it is given).
    const sceneTex = back.kind === 3 && back.sceneKey ? scenes.get(back.sceneKey) ?? null : null;
    const kind = back.kind === 3 && !sceneTex ? 0 : back.kind;
    bindUnit(0, photo); bindUnit(1, mask);
    bindUnit(6, shadow ?? zeroShadow); bindUnit(7, sceneTex ?? zeroScene);
    const g0 = job.garmentColor, t0 = job.garment ?? job.garmentColor;
    gl.uniform1i(uBase.uRecolor, job.garment ? 1 : 0);
    gl.uniform3f(uBase.uGarment, g0[0], g0[1], g0[2]);
    gl.uniform3f(uBase.uTarget, t0[0], t0[1], t0[2]);
    gl.uniform1i(uBase.uBackdrop, kind);
    gl.uniform3f(uBase.uBackA, back.a[0], back.a[1], back.a[2]);
    gl.uniform3f(uBase.uBackB, back.b[0], back.b[1], back.b[2]);
    gl.uniform1f(uBase.uShadowK, kind === 0 ? 0 : job.shadowStrength);
    // The FBO's height, which is the height the CPU's gradient runs over: `maps.width × maps.height`
    // IS the size `renderMockup` composited at whenever the photo needed no downscale.
    gl.uniform1i(uBase.uHeight, height);
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    // Passes 1..n: one print each, reading the previous target and writing the other.
    gl.useProgram(printProg);
    bindUnit(1, mask); bindUnit(2, luma); bindUnit(3, grad); bindUnit(4, residual);
    const look = job.look;
    gl.uniform4f(uPrint.uLook, look.shadowDepth, look.highlight, job.texture * look.texture, look.opacity);
    gl.uniform1f(uPrint.uLmid, job.lmid);
    gl.uniform1f(uPrint.uDispPx, job.dispPx);
    let src = 0;
    for (const p of job.prints) {
      const art = arts.get(p.artId);
      // An art the caller has not uploaded (yet) is skipped rather than drawn from a stale texture:
      // `glJobFor` already dropped the prints whose art it did not know about.
      if (!p.enabled || !art) continue;
      const from = src === 0 ? a : b, to = src === 0 ? b : a;
      gl.bindFramebuffer(gl.FRAMEBUFFER, to.fb);
      bindUnit(0, from.tex);
      bindUnit(5, art);
      gl.uniformMatrix3fv(uPrint.uHinv, false, p.hInv);
      gl.uniform2f(uPrint.uAreaIn, p.areaIn[0], p.areaIn[1]);
      gl.uniform4f(uPrint.uRect, p.rect[0], p.rect[1], p.rect[2], p.rect[3]);
      gl.uniform2f(uPrint.uRot, p.cos, p.sin);
      gl.uniform2f(uPrint.uArtSize, p.artSize[0], p.artSize[1]);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      src = 1 - src;
    }
    last = src;

    // The blit: the finished target onto the canvas, flipped (see gl-shaders.ts's header).
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, width, height);
    gl.useProgram(blitProg);
    bindUnit(0, (last === 0 ? a : b).tex);
    gl.uniform1i(uBlit.uHeight, height);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.bindVertexArray(null);
    if (gl.isContextLost()) { markLost(); return false; }
    return true;
  };

  const readPixels = (): Uint8ClampedArray => {
    const out = new Uint8Array(Math.max(0, width * height * 4));
    const t = targets[last];
    // Read the FBO, not the canvas: the FBO is top-down as it stands (the canvas is the flipped one).
    if (!dead() && ready && t) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, t.fb);
      gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, out);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    }
    return new Uint8ClampedArray(out.buffer);
  };

  const dispose = (): void => {
    if (disposed) return;
    disposed = true;
    target.removeEventListener('webglcontextlost', onLost);
    target.removeEventListener('webglcontextrestored', onRestored);
    lostFns.length = 0;
    if (lost) { arts.clear(); scenes.clear(); targets[0] = targets[1] = null; return; }   // a lost context's objects are already gone
    for (const t of [photo, mask, luma, grad, residual, shadow, zeroShadow, zeroScene]) if (t) gl.deleteTexture(t);
    photo = mask = luma = grad = residual = shadow = null;
    for (const t of arts.values()) gl.deleteTexture(t);
    arts.clear();
    for (const t of scenes.values()) gl.deleteTexture(t);
    scenes.clear();
    deleteTargets();
    if (vao) gl.deleteVertexArray(vao);
    gl.deleteProgram(baseProg); gl.deleteProgram(printProg); gl.deleteProgram(blitProg);
    ready = false;
  };

  return {
    canvas,
    setMaps, setShadow, setArt, dropArt, setScene, dropScene, render, readPixels, dispose,
    // A subscriber that arrives after the loss still hears about it — it has the same work to do.
    onContextLost(fn: () => void): void { if (lost) fn(); else lostFns.push(fn); },
  };
}
